import { gsap } from 'gsap';
import type { Container } from 'pixi.js';
import { DESIGN } from './runtime.js';

interface CameraState {
  x: number;
  y: number;
  zoom: number;
  rotation: number;
  tiltX: number;
  tiltY: number;
}

/**
 * The camera.
 *
 * The brief is explicit that the camera is never static, so the rig always has
 * at least a slow drift running underneath whatever a scene asked for. Scenes
 * request intent (`frame`, `push`, `orbit`, `shake`) and the rig owns the
 * easing, which keeps every move on the same house curve.
 *
 * The world container is transformed rather than the stage, so screen-space
 * chrome - the HUD, the lens flare - stays put while the world moves.
 */
export class CameraRig {
  private readonly state: CameraState = {
    x: DESIGN.width / 2,
    y: DESIGN.height / 2,
    zoom: 1,
    rotation: 0,
    tiltX: 0,
    tiltY: 0,
  };

  private shakeAmount = 0;
  private shakeDecay = 0;
  private driftPhase = Math.random() * Math.PI * 2;
  private driftStrength = 1;
  private orbitSpeed = 0;
  private orbitRadius = 0;
  private orbitAngle = 0;

  /** Set by moves; the render loop reads it to decide on motion blur. */
  private velocity = 0;
  private lastX = DESIGN.width / 2;
  private lastY = DESIGN.height / 2;

  constructor(private readonly world: Container) {
    world.pivot.set(DESIGN.width / 2, DESIGN.height / 2);
    world.position.set(DESIGN.width / 2, DESIGN.height / 2);
  }

  /** Pixels of camera movement in the last frame - drives the motion blur. */
  get speed(): number {
    return this.velocity;
  }

  /** Move and zoom in one gesture. */
  frame(
    x: number,
    y: number,
    zoom: number,
    duration = 1.6,
    ease = 'power3.inOut',
  ): gsap.core.Tween {
    return gsap.to(this.state, { x, y, zoom, duration, ease, overwrite: 'auto' });
  }

  /** Fast punch-in used on strikes and reveals. */
  push(zoom: number, duration = 0.5): gsap.core.Tween {
    return gsap.to(this.state, { zoom, duration, ease: 'power2.out', overwrite: 'auto' });
  }

  pan(x: number, y: number, duration = 2.2, ease = 'sine.inOut'): gsap.core.Tween {
    return gsap.to(this.state, { x, y, duration, ease, overwrite: 'auto' });
  }

  roll(rotation: number, duration = 2): gsap.core.Tween {
    return gsap.to(this.state, { rotation, duration, ease: 'sine.inOut' });
  }

  /** Perspective-ish lean, faked with a small skew on the world container. */
  tilt(tiltX: number, tiltY: number, duration = 1.6): gsap.core.Tween {
    return gsap.to(this.state, { tiltX, tiltY, duration, ease: 'sine.inOut' });
  }

  /** Continuous circular movement around the current centre. */
  orbit(radius: number, speed: number): void {
    this.orbitRadius = radius;
    this.orbitSpeed = speed;
  }

  stopOrbit(): void {
    gsap.to(this, { orbitRadius: 0, duration: 1.2, ease: 'sine.out' });
    this.orbitSpeed = 0;
  }

  /** Impact shake. `amount` is in design pixels. */
  shake(amount = 18, decay = 3.4): void {
    this.shakeAmount = Math.max(this.shakeAmount, amount);
    this.shakeDecay = decay;
  }

  /** Ambient life. 0 for a locked-off ceremony shot, 1+ for a busy arena. */
  setDrift(strength: number): void {
    gsap.to(this, { driftStrength: strength, duration: 1.5, ease: 'sine.inOut' });
  }

  reset(duration = 1.2): void {
    this.stopOrbit();
    gsap.to(this.state, {
      x: DESIGN.width / 2,
      y: DESIGN.height / 2,
      zoom: 1,
      rotation: 0,
      tiltX: 0,
      tiltY: 0,
      duration,
      ease: 'power2.inOut',
      overwrite: 'auto',
    });
  }

  update(deltaSeconds: number, elapsedSeconds: number): void {
    this.driftPhase += deltaSeconds * 0.32;
    this.orbitAngle += deltaSeconds * this.orbitSpeed;

    const driftX = Math.sin(this.driftPhase) * 16 * this.driftStrength;
    const driftY = Math.cos(this.driftPhase * 0.73) * 11 * this.driftStrength;
    const breathe = 1 + Math.sin(elapsedSeconds * 0.42) * 0.012 * this.driftStrength;

    const orbitX = Math.cos(this.orbitAngle) * this.orbitRadius;
    const orbitY = Math.sin(this.orbitAngle) * this.orbitRadius * 0.42;

    if (this.shakeAmount > 0.01) {
      this.shakeAmount = Math.max(0, this.shakeAmount - this.shakeDecay * deltaSeconds * 60 * 0.05);
    } else {
      this.shakeAmount = 0;
    }

    const shakeX = this.shakeAmount > 0 ? (Math.random() - 0.5) * this.shakeAmount * 2 : 0;
    const shakeY = this.shakeAmount > 0 ? (Math.random() - 0.5) * this.shakeAmount * 2 : 0;
    const shakeRot = this.shakeAmount > 0 ? (Math.random() - 0.5) * this.shakeAmount * 0.0016 : 0;

    const px = this.state.x + driftX + orbitX + shakeX;
    const py = this.state.y + driftY + orbitY + shakeY;

    this.velocity = Math.hypot(px - this.lastX, py - this.lastY) / Math.max(deltaSeconds, 0.0001);
    this.lastX = px;
    this.lastY = py;

    this.world.pivot.set(px, py);
    this.world.scale.set(this.state.zoom * breathe);
    this.world.rotation = this.state.rotation + shakeRot;
    this.world.skew.set(this.state.tiltX, this.state.tiltY);
  }
}
